// The webhook vocabulary, keyed by SOURCE: each source brings its own
// triggers (with what kind of target each wants) and its own joins, so adding
// Accounts or Contacts is one more entry here and the form, the list row and
// the detail view re-shape themselves. The form, the list and the detail all
// read this table, so a value never shows as its raw enum ("issues") on one
// screen and as its label ("Projects") on another.

/** The names a stage target resolves to, for the sentence. */
export type WebhookTargetNames = { workflow: string | null; stage: string | null };

export type WebhookTriggerOption = {
  value: string;
  /** Sentence case, system noun capitalized: "Project stage change". */
  label: string;
  /** What the trigger needs pinned: a stage, or nothing. */
  target: "stage" | null;
  /** The plain-words restatement the form shows live and the detail view
   *  reads back. `target` is null while nothing is picked yet. */
  describe: (target: WebhookTargetNames | null) => string;
};

export type WebhookSource = {
  value: string;
  label: string;
  /** The capitalized system noun the sentences use. */
  noun: string;
  triggers: readonly WebhookTriggerOption[];
  joins: readonly { key: string; label: string }[];
};

function inStage(target: WebhookTargetNames | null): string {
  if (!target?.stage) return "";
  return target.workflow ? ` ${target.stage} in ${target.workflow}` : ` ${target.stage}`;
}

export const WEBHOOK_SOURCES: readonly WebhookSource[] = [
  {
    value: "issues",
    label: "Projects",
    noun: "Project",
    triggers: [
      {
        value: "issueStageChange",
        label: "Project stage change",
        target: "stage",
        describe: (t) =>
          t?.stage ? `Sends when a Project moves to${inStage(t)}.` : "Sends when a Project moves to the stage you pick.",
      },
      {
        value: "issueCreatedOnStage",
        label: "Project created on stage",
        target: "stage",
        describe: (t) =>
          t?.stage ? `Sends when a Project is created in${inStage(t)}.` : "Sends when a Project is created in the stage you pick.",
      },
      {
        value: "issueDeleted",
        label: "Project deleted",
        target: null,
        describe: () => "Sends when a Project is deleted.",
      },
      {
        value: "issueTrashed",
        label: "Project trashed",
        target: null,
        describe: () => "Sends when a Project is moved to Trash.",
      },
    ],
    joins: [
      { key: "customFields", label: "Custom fields" },
      { key: "createdBy", label: "Created by" },
      { key: "lastUpdatedBy", label: "Last updated by" },
    ],
  },
];

export const HTTP_METHODS = [
  { label: "GET", value: "get" },
  { label: "POST", value: "post" },
  { label: "PUT", value: "put" },
  { label: "PATCH", value: "patch" },
  { label: "DELETE", value: "delete" },
] as const;

// Base UI's Select is value-based (no `{value,label}` Option object), so the
// trigger renders the label by looking the value up in its own option list.
export function optionLabel(
  options: readonly { value: string; label: string }[],
  val: string | null | undefined,
  fallback: string,
): string {
  return options.find((o) => o.value === val)?.label ?? fallback;
}

/** The source for a stored `resource`; the first source for an unknown one,
 *  so a legacy row still renders. */
export function webhookSource(resource: string | null | undefined): WebhookSource {
  return WEBHOOK_SOURCES.find((s) => s.value === resource) ?? WEBHOOK_SOURCES[0]!;
}

export function triggerOption(
  resource: string | null | undefined,
  trigger: string | null | undefined,
): WebhookTriggerOption | null {
  return webhookSource(resource).triggers.find((t) => t.value === trigger) ?? null;
}

export function triggerLabel(resource: string | null | undefined, trigger: string): string {
  return triggerOption(resource, trigger)?.label ?? trigger;
}

export function triggerNeedsStage(
  resource: string | null | undefined,
  trigger: string | null | undefined,
): boolean {
  return triggerOption(resource, trigger)?.target === "stage";
}

/** The labels of the joins a webhook sends, in the source's order. */
export function joinLabels(resource: string | null | undefined, joins: unknown): string[] {
  if (!joins || typeof joins !== "object") return [];
  const on = joins as Record<string, unknown>;
  return webhookSource(resource).joins.filter((j) => on[j.key] === true).map((j) => j.label);
}

/** The plain-words sentence for a webhook's event. */
export function describeWebhook(
  resource: string | null | undefined,
  trigger: string | null | undefined,
  target: WebhookTargetNames | null,
): string | null {
  return triggerOption(resource, trigger)?.describe(target) ?? null;
}

/** Resolves a stored stage pin to names through the workspace maps. */
export function webhookTargetNames(
  stageId: string | null | undefined,
  stageMap: ReadonlyMap<string, { name: string; workflowId?: string | null }>,
  workflowMap: ReadonlyMap<string, { name?: string | null }>,
): WebhookTargetNames | null {
  if (!stageId) return null;
  const stage = stageMap.get(stageId);
  if (!stage) return null;
  const workflow = stage.workflowId ? (workflowMap.get(stage.workflowId)?.name || null) : null;
  return { stage: stage.name, workflow };
}

/** "Workflow / Stage" for a compact readout, or null when unpinned. */
export function targetLabel(target: WebhookTargetNames | null): string | null {
  if (!target?.stage) return null;
  return target.workflow ? `${target.workflow} / ${target.stage}` : target.stage;
}

/** The URL without its scheme, for a row that already knows it is a URL. */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, "");
}

/** The stored `when.stageId`, read defensively: the column is untyped. */
export function pinnedStageId(when: unknown): string | null {
  if (!when || typeof when !== "object") return null;
  const id = (when as Record<string, unknown>).stageId;
  return typeof id === "string" ? id : null;
}

// The server's rule, said early: a webhook URL must be a full https address.
// The server also refuses a host that resolves to a private address.
export function webhookUrlProblem(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return "Enter a full URL, starting with https://";
  }
  return url.protocol === "https:" ? null : "The URL must start with https://";
}
